import { useQuery } from '@apollo/client/react/hooks';
import { type FunctionComponent } from 'react';
import { type RecommendationsQuery, type RecommendationsQueryVariables } from '../../../__generated__/graphql-client-types';
import { defaultCloudinaryOptions } from '../../../constants/images';
import { buildGTMFindingMethodDynamicRecommendation } from '../../../helpers/analytics/gtm/search-event-builders';
import { useRouterLocation } from '../../../hooks/routing/location.hook';
import { RECOMMENDATIONS, REPORT_RECOMMENDATION_ENGAGEMENT } from '../../../queries/dynamic-yield/dynamic-yield.queries';
import { doesWindowExist } from '../../../utils/dom/dom.utils';
import { ProductCard } from '../../product-components/product-card/product-card.component';
import { dynamicYieldCards, dynamicYieldCardsContainer } from './dy-product-recommendations.css';

export type DyProductRecommendationsProps = {
	campaign: string;
	variantId: number;
	trackingId: string;
};

export const DyProductRecommendations: FunctionComponent<DyProductRecommendationsProps> = ({ campaign, variantId }) => {
	const { pathname } = useRouterLocation();
	const { data: recommendationsData } = useQuery<RecommendationsQuery, RecommendationsQueryVariables>(RECOMMENDATIONS, {
		variables: {
			id: variantId,
			campaign,
			location: pathname,
			usePlaLinks: true
		},
		skip: !doesWindowExist()
	});

	const scrollToTop = () => window.scrollTo(0, 0);

	const recommendationsList = recommendationsData?.recommendations;

	return (
		<section data-testid={`dy-${campaign}-${variantId}`} style={{ minHeight: '380.5px' }}>
			<div className={'ph0 ph3-ns pt3 pb2 f4 b tl bb-ns b--theme-grey-light'}>Recommended for You</div>
			<div
				className={`f5 f6-m nowrap ws-normal-ns flex-ns flex-wrap-ns mt4-ns mh0 ph0 overflow-x-auto scroll-snap-x overflow-x-hidden-ns overflow-y-hidden ${dynamicYieldCardsContainer}`}>
				{recommendationsList?.map((dyRec, index) => (
					// click handler is only being used for analytics
					// eslint-disable-next-line jsx-a11y/click-events-have-key-events
					<div
						role="button"
						key={`dyrec_${index}`}
						className={`dib db-ns v-top overflow-hidden ${dynamicYieldCards} ma0`}
						onClick={scrollToTop}
						data-testid="dyRecommendation"
						tabIndex={0}>
						<ProductCard
							image={{
								description: dyRec.image.description,
								publicID: dyRec.image.id,
								options: defaultCloudinaryOptions,
								useDimensions: false
							}}
							brand={dyRec.brandName}
							title={dyRec.title}
							url={dyRec.url}
							serverAnalytics={{
								mutation: REPORT_RECOMMENDATION_ENGAGEMENT,
								variables: {
									slotId: dyRec.id
								}
							}}
							analyticsEvent={{ data: buildGTMFindingMethodDynamicRecommendation() }}
							ratingValue={dyRec.ratingValue}
							priceInfo={{ current: dyRec.price }}
						/>
					</div>
				))}
			</div>
		</section>
	);
};
